import axios from 'axios';
import { recordShownError } from '../observability';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Toast, { type ToastKind } from '../components/shared/Toast';
import { apiError } from './useApiCall';
import { isSignOutRejection } from '../lib/signOuts';

// ── useToast — app-wide toast primitive ──────────────────────────────────────
// Single source of truth for transient user-facing messages. Consumers call
// useToast() and get { showToast, showError, showWarning, clearToast }.
//
// Boundary rule (see CLAUDE.md + agent-docs/code-patterns.md § Toast kinds):
// <ToastProvider> wraps LayoutRoutes only. RingDisplay deliberately mounts
// OUTSIDE the provider — no user at the TV to dismiss a popup, errors go
// to telemetry. useToast() THROWS outside the provider. That's the
// intentional guard against future accidental imports of toast-emitting
// components into the RingDisplay subtree.
//
// Queue policy: the rank of the kind decides (#557 review). The rank is
// error > warning > success = info. A toast of the same rank or of a higher
// rank replaces the visible toast, so the last one wins. A toast of a lower
// rank does not replace it. It waits, and it shows when the visible toast
// hides. Before, the last call always won. With two replicas, the socket
// event `weighin:ended` can arrive after the answer of End Weigh-in, and its
// success toast replaced the BOTH_ATHLETES_ABSENT warning of the answer.
// A waiting toast replaces a waiting toast of the same rank, so at most two
// wait (a warning and a success). A toast that waited longer than
// MAX_WAIT_MS does not show: its moment is gone. An empty message is not a
// toast. The visual `<Toast>` handles its own auto-hide.
//
// #666 — A toast hides after 3 s. `showToast(text, kind, { untilClosed: true })`
// keeps it until the user closes it, and the toast shows a close button. Only
// the warning of a sign-out that the server did not confirm uses it: the page
// goes to Home at the same moment, and the user must read the text. A toast of
// a lower rank that waits behind it shows when the user closes it, if it
// waited MAX_WAIT_MS or less.

/** The options of one toast. */
export interface ToastOptions {
  /** #666 — The toast stays until the user closes it: no auto-hide. */
  untilClosed?: boolean;
}

interface ToastItem {
  /** Unique for each call: a dismiss hides only its own toast. */
  id: number;
  message: string;
  kind: ToastKind;
  /** Date.now() when the toast arrived. */
  at: number;
  /** The toast stays until the user closes it (`ToastOptions`). */
  untilClosed: boolean;
}

/** The visible toast, and the toasts of a lower rank that wait behind it. */
interface ToastQueue {
  current: ToastItem | null;
  waiting: ToastItem[];
}

const RANK: Record<ToastKind, number> = { error: 2, warning: 1, success: 0, info: 0 };
/** A waiting toast older than this does not show. */
const MAX_WAIT_MS = 8000;

/** Add a toast to the queue. A toast of the same rank or of a higher rank
 *  replaces the visible toast. A toast of a lower rank waits, and it replaces
 *  a waiting toast of its own rank. */
function addToast(q: ToastQueue, next: ToastItem): ToastQueue {
  if (!next.message) return q;
  if (!q.current || RANK[next.kind] >= RANK[q.current.kind]) return { current: next, waiting: q.waiting };
  const waiting = q.waiting.filter(w => RANK[w.kind] !== RANK[next.kind]);
  return { current: q.current, waiting: [...waiting, next] };
}

/** Hide the visible toast. With `id`, hide it only when it is still the
 *  visible toast: a timer or a click of a toast that was replaced does
 *  nothing. The waiting toasts that are not too old arrive again in their
 *  order, so the same rule applies to them. */
function hideToast(q: ToastQueue, id?: number): ToastQueue {
  if (id !== undefined && q.current?.id !== id) return q;
  const now = Date.now();
  return q.waiting.filter(w => now - w.at <= MAX_WAIT_MS).reduce(addToast, { current: null, waiting: [] });
}

export interface ToastAPI {
  /** Generic: message + the kind. Mandatory — the component never guesses it from the message.
   *  `options.untilClosed` keeps the toast until the user closes it (#666). */
  showToast: (message: string, kind: ToastKind, options?: ToastOptions) => void;
  /** Shortcut for API errors — runs `apiError(err)` to get the translated
   *  message, then shows as kind='error'. Replaces the common
   *  `setMsg(apiError(err))` and `alert(apiError(err))` patterns. */
  showError: (err: unknown) => void;
  /** A completed action: shows as kind='success' (green). */
  showSuccess: (message: string) => void;
  /** Amber warning toast — for non-blocking `response.warnings[]` items
   *  and IFMA tiebreak hints. */
  showWarning: (message: string) => void;
  /** Manually dismiss the current toast (rarely needed — auto-hide handles
   *  the common case). A toast that waits shows next. */
  clearToast: () => void;
}

const ToastContext = createContext<ToastAPI | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<ToastQueue>({ current: null, waiting: [] });
  const nextId = useRef(0);

  // The id and the time are set here, outside the state updater, so that a
  // second run of the updater (StrictMode) adds the same toast.
  const push = useCallback((message: string, kind: ToastKind, options?: ToastOptions) => {
    const item: ToastItem = { id: ++nextId.current, message, kind, at: Date.now(), untilClosed: options?.untilClosed ?? false };
    setQueue(q => addToast(q, item));
  }, []);

  const showToast = useCallback((message: string, kind: ToastKind, options?: ToastOptions) => push(message, kind, options), [push]);

  const showError = useCallback((err: unknown) => {
    // #666 — A sign-out ended the request. The user signs out, so no toast shows.
    if (isSignOutRejection(err)) return;
    const message = apiError(err);
    push(message, 'error');
    // The UI's half of the telemetry: what the user saw (observability.ts).
    recordShownError(err, 'toast');
  }, [push]);

  const showSuccess = useCallback((message: string) => push(message, 'success'), [push]);
  const showWarning = useCallback((message: string) => push(message, 'warning'), [push]);

  const clearToast = useCallback(() => {
    setQueue(q => hideToast(q));
  }, []);

  // The auto-hide timer and the click of <Toast> hide only the toast that
  // they belong to. A new toast gives a new callback, so its timer starts
  // with its full time.
  const currentId = queue.current?.id;
  const dismissCurrent = useCallback(() => {
    setQueue(q => hideToast(q, currentId));
  }, [currentId]);

  // Safety net (2026-09-19). A rejected API promise that nobody caught — a
  // `.then()` chain with no `.catch()` — used to vanish: the backend had
  // answered 400 with a code, and the user saw nothing. Inside the provider
  // it becomes a toast. A caught error never reaches this listener, so
  // nothing is shown twice; RingDisplay mounts no provider, so the TV
  // surface stays silent, as CLAUDE.md requires. Only an HTTP response is
  // shown — a network error mid-retry is the axios-retry layer's business.
  useEffect(() => {
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      if (axios.isAxiosError(reason) && reason.response) {
        e.preventDefault();
        showError(reason);
      }
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, [showError]);

  // Memoised (2026-09-19). A fresh object each render made the context value
  // change on every toast show and hide, and once the whole tournament tree
  // consumed useToast() that re-rendered every consumer on each toast — the
  // check-in list detached its rows under a click ("Element is not attached
  // to the DOM"). The callbacks are stable, so the value is too.
  const api: ToastAPI = useMemo(() => ({ showToast, showError, showSuccess, showWarning, clearToast }), [showToast, showError, showSuccess, showWarning, clearToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Toast
        message={queue.current?.message ?? ''}
        kind={queue.current?.kind ?? 'info'}
        untilClosed={queue.current?.untilClosed ?? false}
        onDismiss={dismissCurrent}
      />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastAPI {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      'useToast() called outside <ToastProvider>. ' +
      'RingDisplay deliberately mounts outside the provider — no toasts ' +
      'on unattended TV surfaces. If you hit this error, either (a) lift ' +
      'the component out of the RingDisplay subtree, or (b) extract the ' +
      'toast call to a prop so RingDisplay can pass a no-op. ' +
      'Do NOT widen <ToastProvider> to cover RingDisplay.'
    );
  }
  return ctx;
}
